import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { ChartColumnStacked, CheckCircle, UndoIcon } from "lucide-react";

const { Text } = Typography;
import { FONT } from "constants/index";
import { toRoman } from "views/util/roman";
import { updateTrainingAquatic } from "services/aquatic/aquatic-service";
import { notificationErroInternal, notificationSucess } from "util/api-utils";
import DrawerApp from "components/drawer-app";
import ProgressoRadarChart from "./managerial-chart-aqua";

const DetailAquatic = ({
  user,
  findSubphaseById,
  calcPercent,
  allPhases = [],
  saveTask,
}) => {
  const [loadingData, setLoadingData] = useState(false);
  const [phaseActual, setPhaseActual] = useState({
    phase: { subphases: [] },
    subphase: {},
  });
  const [form] = Form.useForm();
  const [phases, setPhases] = useState([{ subphases: [] }]);
  const [phasesOptions, setPhasesOptions] = useState([]);
  const [subPhasesOptions, setSubPhasesOptions] = useState([]);
  const [openChart, setOpenChart] = useState(false);
  const [openTask, setOpenTask] = useState(false);
  const [userTasks, setUserTask] = useState([]);

  useEffect(() => {
    console.log("user", user);
    setLoadingData(true);
    setPhases(allPhases);
    const phasesOptions = allPhases.map((item) => {
      return { label: item.name, value: item.id };
    });
    setPhasesOptions(phasesOptions);
    const phaseActual = findSubphaseById(user?.training?.subphasesId);
    const subPhasesOptions = phaseActual?.phase?.subphases?.map((item) => {
      return { label: item.name, value: item.id, order: item.order };
    });
    setSubPhasesOptions(subPhasesOptions);
    setPhaseActual(phaseActual);
    const evolution = calcPercent(
      user?.tasksDone ?? [],
      phaseActual?.subphase?.objectives ?? []
    );

    const idSubPhase =
      evolution < 34 ? 1 : evolution > 33 && evolution < 67 ? 2 : 3;
    const subPhaseName = subPhasesOptions?.find(
      (item) => item.order == idSubPhase
    );
    form.setFieldsValue({
      phaseId: phaseActual?.phase?.id,
      subphaseId: subPhaseName?.label,
    });
    setLoadingData(false);
  }, [user, allPhases]);

  useEffect(() => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith("exercise-scroll-aqua-detail")) {
        sessionStorage.removeItem(key);
      }
    });
  }, []);

  const DescriptionItem = ({ title, style }) => (
    <div
      className="site-description-item-profile-wrapper"
      style={
        style ? style : { fontFamily: FONT, fontSize: 14, fontWeight: 500 }
      }
    >
      {title}
    </div>
  );

  const changePhase = (value) => {
    const phasesActual = phases.find((item) => item.id == value);
    const phaseActual = findSubphaseById(phasesActual.subphases[0].id);

    setPhaseActual(phaseActual);
    const subPhasesOptions = phaseActual.phase.subphases.map((item) => {
      return { label: item.name, value: item.id, order: item.order };
    });
    setSubPhasesOptions(subPhasesOptions);
    const evolution = calcPercent(
      user?.tasksDone ?? [],
      phaseActual?.subphase?.objectives ?? []
    );
    const idSubPhase =
      evolution < 34 ? 1 : evolution > 33 && evolution < 67 ? 2 : 3;
    const subPhaseName = subPhasesOptions?.find(
      (item) => item.order == idSubPhase
    );
    form.setFieldsValue({
      subphaseId: subPhaseName?.label,
    });
  };

  const saveTaskDetail = async (status, taskId, userId) => {
    setLoadingData(true);

    try {
      await saveTask(status, taskId, userId);
    } catch (error) {
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde."
      );
    } finally {
      setLoadingData(false);
    }
  };
  const handleSave = async () => {
    setLoadingData(true);
    const formData = form.getFieldsValue();
    const phaseId = formData.phaseId;
    try {
      const urlSave = user.training.id + "/" + phaseId;
      await updateTrainingAquatic(urlSave);
      notificationSucess();
    } catch (error) {
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde."
      );
    } finally {
      setLoadingData(false);
    }
  };

  const onCloseChart = () => {
    setOpenChart(false);
  };
  const closeDrawerTask = () => {
    setUserTask([]);
    setOpenTask(false);
  };

  const Exercises = ({ exercises, userId, tasksDone = [] }) => {
    const scrollRef = useRef(null);
    const storageKey = `exercise-scroll-aqua-detail-${userId}`;

    useLayoutEffect(() => {
      const savedScroll = sessionStorage.getItem(storageKey);
      if (scrollRef.current && savedScroll) {
        scrollRef.current.scrollTop = parseInt(savedScroll, 10);
      }
    }, [storageKey]);

    const handleScroll = () => {
      if (scrollRef.current) {
        sessionStorage.setItem(
          storageKey,
          scrollRef.current.scrollTop.toString()
        );
      }
    };
    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          overflowY: "auto",
          overflowX: "auto",
        }}
        className="custom-scroll"
      >
        {exercises.map((exercise, index) => {
          const allTasksExecuted = exercise.tasks.every((task) =>
            tasksDone.some((done) => done.taskId === task.id)
          );

          const executedTasks = exercise.tasks.filter((task) =>
            tasksDone.some((done) => done.taskId === task.id)
          );
          const highest =
            executedTasks.length > 0
              ? executedTasks.reduce((max, task) =>
                  task.order > max.order ? task : max
                ).order + 1
              : 1;

          const task = exercise.tasks.find((item) => item.order === highest);
          const roman = highest > 0 ? toRoman(highest) : "I";
          const previousTask =
            highest > 1
              ? exercise.tasks.find((item) => item.order === highest - 1)
              : null;

          const previousTaskId = previousTask?.id || null;

          return (
            <Col style={{ minWidth: 350 }}>
              <Row key={index}>
                <Col span={24}>
                  <Card
                    style={{
                      backgroundColor: "#ffff",
                      margin: 5,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setUserTask(exercise.tasks);
                      setOpenTask(true);
                    }}
                  >
                    <Row justify="space-between" align="middle">
                      <Col>
                        <DescriptionItem title={exercise.name} />
                      </Col>
                      {!allTasksExecuted && (
                        <Col>
                          <DescriptionItem title={roman} />
                        </Col>
                      )}
                    </Row>
                    {allTasksExecuted && (
                      <DescriptionItem
                        title={"Todas as tarefas concluídas!"}
                        style={{
                          fontFamily: FONT,
                          fontSize: 13,
                          fontWeight: 300,
                          color: "#09ac9c",
                        }}
                      />
                    )}
                    {!allTasksExecuted && (
                      <>
                        <DescriptionItem
                          title={task?.name}
                          style={{
                            fontFamily: FONT,
                            fontSize: 13,
                            fontWeight: 300,
                          }}
                        />
                        {saveTask && (
                          <Space>
                            {previousTaskId && (
                              <Button
                                type="primary"
                                style={{
                                  backgroundColor: "#fff1f0",
                                  borderColor: "#fff",
                                  color: "#ff4d4f",
                                  borderRadius: 12,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();

                                  saveTaskDetail(
                                    "STANDY_BY",
                                    previousTaskId,
                                    userId
                                  );
                                }}
                                icon={
                                  <UndoIcon
                                    size={18}
                                    style={{
                                      marginBottom: "-3px",
                                      marginRight: "7px",
                                    }}
                                  />
                                }
                              >
                                Desfazer
                              </Button>
                            )}

                            <Button
                              type="primary"
                              style={{
                                backgroundColor: "#e6fffb",
                                borderColor: "#fff",
                                color: "#09ac9c",
                                borderRadius: 12,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                saveTaskDetail("DONE", task?.id, userId);
                              }}
                              icon={
                                <CheckCircle
                                  size={18}
                                  style={{
                                    marginBottom: "-3px",
                                    marginRight: "7px",
                                  }}
                                />
                              }
                            >
                              Concluir
                            </Button>
                          </Space>
                        )}
                      </>
                    )}
                  </Card>
                </Col>
              </Row>
            </Col>
          );
        })}
      </div>
    );
  };
  const Tasks = ({ tasks }) => {
    return (
      <div
        style={{
          overflowY: "auto",
          overflowX: "auto",
        }}
        className="custom-scroll"
      >
        {tasks.map((task, index) => {
          const roman = task.order > 0 ? toRoman(task.order) : "I";

          return (
            <Col style={{ minWidth: 350 }}>
              <Row key={index}>
                <Col span={24}>
                  <Card style={{ backgroundColor: "#ffff", margin: 5 }}>
                    <Row justify="space-between" align="middle">
                      <Col>
                        <DescriptionItem title={task.name} />
                      </Col>
                      <Col>
                        <DescriptionItem title={roman} />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Col>
          );
        })}
      </div>
    );
  };
  return (
    <>
      <Spin spinning={loadingData}>
        <div style={{ fontFamily: FONT, fontWeight: 400 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <p>{phaseActual?.phase?.generalObjective}</p>
            </Col>
            <Col>
              <Space>
                <div
                  style={{ cursor: "pointer", color: "#09ac9c" }}
                  onClick={() => {
                    setOpenChart(true);
                  }}
                >
                  <ChartColumnStacked />
                </div>
                {saveTask && (
                  <Button
                    style={{ fontFamily: FONT }}
                    onClick={handleSave}
                    type="primary"
                  >
                    Salvar
                  </Button>
                )}
              </Space>
            </Col>
          </Row>

          <Form
            form={form}
            layout="vertical"
            style={{ paddingTop: 20, paddingBottom: 20 }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="phaseId"
                  label={<span style={{ fontFamily: FONT }}>Nível</span>}
                  style={{ height: "40px" }}
                  rules={[
                    { required: true, message: "É necessário informar o Fase" },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    onChange={changePhase}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: "100%" }}
                    options={phasesOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="subphaseId"
                  label={<span style={{ fontFamily: FONT }}>Fase</span>}
                  style={{ height: "40px" }}
                  rules={[
                    {
                      required: true,
                      message: "É necessário informar a Subclassificação",
                    },
                  ]}
                >
                  <Input disabled={true} style={{ fontFamily: FONT }} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <p style={{ fontSize: 18 }}>{phaseActual?.phase?.name}</p>

          <div>
            <Progress
              percent={calcPercent(
                user?.tasksDone ?? [],
                phaseActual?.subphase?.objectives ?? []
              )}
            />
            <Exercises
              exercises={phaseActual?.subphase?.objectives ?? []}
              userId={user?.id}
              tasksDone={user?.tasksDone}
            />
          </div>
        </div>
      </Spin>
      <DrawerApp
        open={openChart}
        onClose={onCloseChart}
        title={user?.name}
        body={
          <ProgressoRadarChart
            phases={phases}
            taskDone={user?.tasksDone ?? []}
          />
        }
        size={"large"}
        isOnlyView={true}
        width={outerWidth < 501 ? "100%" : "65%"}
      />
      <DrawerApp
        open={openTask}
        onClose={closeDrawerTask}
        title={"Atividades"}
        body={<Tasks tasks={userTasks} />}
        width={outerWidth < 501 ? "100%" : "40%"}
        isOnlyView={true}
      />
    </>
  );
};
export default DetailAquatic;
